import * as React from "react";
import * as ReactDOM from "react-dom";
import cns from "classnames";
import { zeroNumber } from "@bonliva-crm/common";
import {
  Button,
  ButtonGroup,
  ClickAwayListener,
  Collapse,
  ListItemIcon,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  withStyles
} from "@material-ui/core";
import { Classes, ThemeType } from "@theme";
import { IconButton } from "./IconButton";
import WithTooltip from "../WithTooltip";

export type DropdownButtonOption = React.ComponentProps<typeof Button> & {
  /**
   * this is different from "disabled" prop, because we might want a user to be able
   * to select a dropdown item, but not to submit it (regular "disabled" prop), or
   * we might want the user to not be able to select an option at all (optionDisabled)
   */
  optionDisabled?: boolean;
  label: string;
  tabIndex: number;
};

type Props = {
  classes: Classes<typeof styles>;
  ArrowButtonProps?: Partial<React.ComponentProps<typeof Button>> & {
    tabIndex: number;
  };
  className?: string;
  disabled?: boolean;
  disableBtn?: boolean;
  initiallySelected?: number;
  size?: "small" | "medium" | "large";
  selected?: number;
  onSelect?: (index: number, option: DropdownButtonOption) => any;
  options: DropdownButtonOption[];
  withPopper?: boolean;
  PopperProps: Partial<React.ComponentProps<typeof Popper>>;
  ButtonProps?: Partial<React.ComponentProps<typeof Button>>;
};

type State = {
  open: boolean;
  selectedIndex: number;
};

class DropdownButton extends React.Component<Props, State> {
  static defaultProps: Partial<Props> = {
    disabled: false,
    size: "small"
  };

  rootRef: HTMLElement;
  portalParentEl: HTMLElement;

  state: State = {
    open: false,
    selectedIndex:
      this.props.selected != null
        ? this.props.selected
        : zeroNumber(this.props.initiallySelected)
  };

  componentDidMount() {
    if (this.props.withPopper) {
      const div = document.createElement("div");
      div.style.position = "absolute";
      div.style.left = "0";
      div.style.right = "0";
      div.style.top = "0";
      div.style.bottom = "0";
      div.style.zIndex = "10000";
      div.style.display = "none";
      document.body.appendChild(div);
      this.portalParentEl = div;
    }
  }

  componentWillUnmount() {
    this.portalParentEl && this.portalParentEl.remove();
  }

  handleToggle = (e) => {
    e.stopPropagation();
    this.setState({ open: !this.state.open }, this.updatePortal);
  };

  handleClose = () => {
    this.setState({ open: false }, this.updatePortal);
  };

  handleSelect = (index: number) => () => {
    this.setState({ selectedIndex: index, open: false }, this.updatePortal);

    if (this.props.onSelect) {
      this.props.onSelect(index, this.props.options[index]);
    }
    if (this.props.options[index].href) {
      // eslint-disable-next-line no-restricted-globals
      location.href = this.props.options[index].href;
    }
  };

  updatePortal = () => {
    if (this.portalParentEl) {
      this.portalParentEl.style.display = this.state.open ? "block" : "none";
    }
  };

  renderDropdownList = () => {
    const { classes, options } = this.props;
    const { open } = this.state;

    const list = (
      <ClickAwayListener onClickAway={this.handleClose}>
        <Paper className="dropdown-button-menu" onClick={(e) => e.stopPropagation()}>
          <MenuList role="menu" className={classes.list}>
            {options.map((opt, i) => (
              <WithTooltip title={opt.title} key={`dropdown-menu-item-${i}`}>
                <MenuItem
                  onClick={!opt.optionDisabled ? this.handleSelect(i) : () => null}
                  disabled={opt.optionDisabled}
                  style={{ pointerEvents: "all" }}
                  className={classes.listItem}
                  id={opt.id}
                >
                  {opt.startIcon && <ListItemIcon>{opt.startIcon}</ListItemIcon>}
                  {opt.label}
                </MenuItem>
              </WithTooltip>
            ))}
          </MenuList>
        </Paper>
      </ClickAwayListener>
    );

    if (!this.props.withPopper) {
      return (
        <div className={classes.dropdown}>
          <Collapse
            in={open}
            onEntered={(node) => (node.style.overflow = "visible")}
            onExit={(node) => (node.style.overflow = "hidden")}
          >
            {list}
          </Collapse>
        </div>
      );
    }

    const el = (
      <Popper anchorEl={this.rootRef} open={open} disablePortal placement="bottom">
        {list}
      </Popper>
    );

    return this.portalParentEl ? ReactDOM.createPortal(el, this.portalParentEl) : null;
  };

  render() {
    const {
      ArrowButtonProps,
      classes,
      options,
      disabled: mainDisabledProp,
      selected,
      disableBtn,
      onSelect,
      PopperProps,
      ...rest
    } = this.props;

    const index = selected != null ? selected : this.state.selectedIndex;

    if (index > options.length - 1) {
      throw new Error("dropdown button not found");
    }

    const option = options[index];

    const { color, label, optionDisabled, ...buttonProps } = option;

    const {
      className: classNameProp,
      disabled: disabledProp,
      onClick,
      ...restButtonProps
    } = buttonProps;

    const disabled = mainDisabledProp || disabledProp || optionDisabled;

    const labelBtnClassName = cns(classes.mainLabelButton, classNameProp, {
      [classes.adjacent]: options.length > 1
    });

    return (
      <div {...rest} ref={(ref) => (this.rootRef = ref)}>
        <ButtonGroup variant="contained" color={color}>
          <Button
            color="default"
            size={this.props.size}
            className={labelBtnClassName}
            disabled={disabled || disableBtn}
            onClick={onClick}
            data-test="DropdownButtonMain"
            {...restButtonProps}
          >
            {label}
          </Button>

          {options.length > 1 && (
            <IconButton
              color="default"
              size={this.props.size}
              disabled={mainDisabledProp}
              onClick={this.handleToggle}
              {...ArrowButtonProps}
            />
          )}
        </ButtonGroup>

        {this.renderDropdownList()}
      </div>
    );
  }
}

const styles = (theme: ThemeType) => ({
  mainLabelButton: {
    boxShadow: "none",
    flexGrow: 1,

    "& > span > span > *:not(:last-child)": {
      marginRight: theme.spacing()
    }
  },

  adjacent: {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0
  },

  leftIcon: {
    marginLeft: -1 * theme.spacing()
  },

  rightIcon: {
    marginRight: -1 * theme.spacing()
  },

  dropdown: {
    position: "absolute",
    top: 40,
    left: 0,
    right: 0,
    zIndex: 2
  },

  list: {
    padding: 0
  },

  listItem: {
    ...theme.typography.button,

    "& svg": {
      marginRight: theme.spacing(1 / 2)
    }
  }
});

export default withStyles(styles as any)(DropdownButton);
